import { useContext, Suspense } from 'react';
import SWSLogo from '@assets/images/logo/sws-logo-updated.svg';
import { Row } from '@simplywallst/ui-core';
import ChevronLeftSolid from '@simplywallst/ui-core/icons/ChevronLeftSolid';
import loadable from '@loadable/component';
import {
  DesktopTopbarBound,
  DesktopTopbarWrapper,
  SWSLogoWrapper,
  MainNavigation,
  SearchWrapper,
  BackButton,
} from './styled';
import useUser from '@/hooks/useUser';
import { useUserAccess } from '@/hooks/useUserAccess';
import LayoutContext from '@/components/Layout/context/LayoutContext/LayoutContext';
import { useTracking } from 'react-tracking';
import { useSelector } from 'react-redux';
import { useFlag } from '@/features/unleash';
import { getCanGoBack } from '@/components/Nav/MobileTopbar/redux/selectors';
import { useLocale } from '@/hooks/useLocale';
import { isPageTranslated } from '@/utils/locale';
import { useLocation } from '@/hooks/useLocation';
import { UpgradeButton } from '@/components/Nav/DesktopTopbar/components/UpgradeButton';

const Nav = loadable(() => import('../Nav'), {
  resolveComponent: (component) => component.Nav,
});

const BasicSearch = loadable(
  () => import('@/components/Nav/DeprecatedSearch/components/BasicSearch'),
  { ssr: false }
);

const DefaultState = loadable(
  () => import('@/components/Nav/DeprecatedSearch/components/DefaultState')
);

const DesktopTopbarAccountAccess = loadable(
  () =>
    import(
      '@/components/Nav/DesktopTopbar/components/DesktopTopbarAccountAccess'
    )
);

const DesktopTopbarLoggedOut = loadable(
  () =>
    import('@/components/Nav/DesktopTopbar/components/DesktopTopbarLoggedOut'),
  { resolveComponent: (c) => c.DesktopTopbarLoggedOut }
);

const LanguageButton = loadable(
  () => import('@/components/Nav/DesktopTopbar/components/LanguageButton')
);

export const TopBar = () => {
  const languageDropdownFlag = useFlag({ name: 'language-dropdown' });
  const locationState = useLocation();
  const showLanguageDropdown =
    languageDropdownFlag.enabled && isPageTranslated(locationState.type);
  const { trackEvent } = useTracking();
  const layout = useContext(LayoutContext);
  const user = useUser();
  const access = useUserAccess();

  const canGoBack = useSelector(getCanGoBack);
  const location = locationState?.type ?? '';

  const locale = useLocale();
  return (
    <DesktopTopbarWrapper
      as="header"
      justifyContent="center"
      data-cy-id="main-navigation-wrapper"
    >
      <DesktopTopbarBound wide={layout.wider}>
        {canGoBack && (
          <BackButton>
            <button
              onClick={() => {
                window.history.back();
              }}
            >
              <ChevronLeftSolid />
            </button>
          </BackButton>
        )}
        <SWSLogoWrapper canGoBack={canGoBack}>
          <a
            href="/?view"
            onClick={() =>
              trackEvent({
                action: 'click',
                modifier: 'logo',
              })
            }
          >
            <SWSLogo />
          </a>
        </SWSLogoWrapper>
        <Row as="nav" style={{ flexWrap: 'nowrap' }}>
          <MainNavigation data-cy-id="main-navigation" canGoBack={canGoBack}>
            <Suspense>
              <Nav />
            </Suspense>
          </MainNavigation>
          <div className="grid grow grid-flow-col grid-cols-[minmax(0,1fr)] gap-x1 pl-x1 pr-[18px]">
            <SearchWrapper>
              <BasicSearch defaultState={<DefaultState />} locale={locale} />
            </SearchWrapper>
            {showLanguageDropdown && <LanguageButton />}
            <UpgradeButton />
            {user.isFetched ? (
              user.data.loggedIn ? (
                <DesktopTopbarAccountAccess
                  location={location}
                  maxViewCompanyReports={
                    access.status === 'success'
                      ? access.data.viewCompanyReports
                      : 0
                  }
                />
              ) : (
                <DesktopTopbarLoggedOut />
              )
            ) : null}
          </div>
        </Row>
      </DesktopTopbarBound>
    </DesktopTopbarWrapper>
  );
};
